'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { PlatformBrand } from '../../utils/platformBrand';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import { DisconnectButton, IntegrationRow, ROW_BUTTON } from './IntegrationList';
type Props = {
  label: string;
  brand: PlatformBrand;
  loading: boolean;
  connected: boolean;
  accountText?: string; // status line when connected; defaults to "Connected"
  supports: string; // what cross-posting sends, shown while disconnected
  note?: ReactNode; // requirements, folded under the row while disconnected
  issue?: boolean; // platform integration is sandboxed/broken (see crosspost.getPlatformHealth)
  autoEnabled: boolean;
  onConnect: () => Promise<void>;
  onDisconnect: () => Promise<void>;
  onToggleAuto: () => void;
};
const AppCard = t0 => {
  const $ = _c(34);
  const {
    label,
    brand,
    loading,
    connected,
    accountText,
    supports,
    note,
    issue,
    autoEnabled,
    onConnect,
    onDisconnect,
    onToggleAuto
  } = t0;
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDisconnect, setShowDisconnect] = useState(false);
  let t1;
  if ($[0] !== onConnect) {
    t1 = async () => {
      setIsConnecting(true);
      await onConnect();
      setIsConnecting(false);
    };
    $[0] = onConnect;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleConnect = t1;
  let t2;
  if ($[2] !== onDisconnect) {
    t2 = async () => {
      setIsDisconnecting(true);
      await onDisconnect();
      setIsDisconnecting(false);
      setShowDisconnect(false);
    };
    $[2] = onDisconnect;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleDisconnect = t2;
  const status = loading ? "Checking\u2026" : connected ? accountText ?? "Connected" : supports;
  let t3;
  if ($[4] !== brand.icon) {
    t3 = <brand.icon size={18} />;
    $[4] = brand.icon;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `${brand.bg} ${brand.color}`;
  let t5;
  if ($[6] !== autoEnabled || $[7] !== connected || $[8] !== handleConnect || $[9] !== isConnecting || $[10] !== label || $[11] !== loading || $[12] !== onToggleAuto) {
    t5 = loading ? undefined : connected ? <><label className="mr-2 flex cursor-pointer items-center gap-2 text-xs text-textSecondary">Auto-post<button type="button" role="switch" aria-checked={autoEnabled} onClick={onToggleAuto} className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${autoEnabled ? "bg-primary" : "bg-textSecondary/30"}`}><span className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow transition-transform duration-200 ${autoEnabled ? "translate-x-4" : "translate-x-0"}`} /></button></label><DisconnectButton label={label} onClick={() => setShowDisconnect(true)} /></> : <Button text="Connect" variant="SECONDARY" classNames={ROW_BUTTON} onClick={() => void handleConnect()} loading={isConnecting} />;
    $[6] = autoEnabled;
    $[7] = connected;
    $[8] = handleConnect;
    $[9] = isConnecting;
    $[10] = label;
    $[11] = loading;
    $[12] = onToggleAuto;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== connected || $[15] !== loading || $[16] !== note) {
    t6 = note && !loading && !connected && <details className="text-xs text-textSecondary/70"><summary className="cursor-pointer select-none text-textSecondary hover:text-textPrimary">Requirements</summary><div className="mt-1.5 space-y-1">{note}</div></details>;
    $[14] = connected;
    $[15] = loading;
    $[16] = note;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== issue || $[19] !== label || $[20] !== status || $[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <IntegrationRow icon={t3} tint={t4} name={label} status={status} limited={issue} actions={t5}>{t6}</IntegrationRow>;
    $[18] = issue;
    $[19] = label;
    $[20] = status;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== handleDisconnect || $[27] !== isDisconnecting || $[28] !== label || $[29] !== showDisconnect) {
    t8 = showDisconnect && <Confirmation title={`Disconnect ${label}`} message={`Are you sure you want to disconnect your ${label} account? You'll need to reconnect to cross-post in the future.`} cancelHandler={() => setShowDisconnect(false)} confirmHandler={handleDisconnect} actionButtonText="Disconnect" variant="DANGER" actionButtonLoading={isDisconnecting} />;
    $[26] = handleDisconnect;
    $[27] = isDisconnecting;
    $[28] = label;
    $[29] = showDisconnect;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t7 || $[32] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
export default AppCard;